import { clearClubSelection, clubCapabilities, confirmedClub } from './club-session';
/**
 * League of Managers - Game Service Facade
 * 
 * Orquesta los microservicios desacoplados de estado:
 * - AuthState: /api/auth/* y /api/users/*
 * - LeaguesState: /api/leagues/* y /api/competition/*
 * - SquadState: /api/clubs/*, /api/squad/* y tácticas
 * - MarketState: /api/market/*
 * - EconomyState: /api/stadium/* y /api/economy/*
 * - UiState: Navegación, tema visual y observabilidad
 */
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthState } from './auth.state';
import { LeaguesState } from './leagues.state';
import { SquadState, TacticalFormation, FORMATION_SLOTS } from './squad.state';
import { MarketState } from './market.state';
import { EconomyState } from './economy.state';
import { UiState, ActiveTab } from './ui.state';
import { CompetitionService } from '../http/competition.service';
import { newsApi } from '../http/news.api';
import { APP_VERSION, APP_BUILD, APP_VERSION_TAG, APP_VERSION_LABEL } from '../version';
import { Matchday, StandingsRow, Player, NewsResponse, ClubShieldConfig, PortfolioItem, ReleaseClauseAttemptPayload, ContractRenewalPayload } from '../models/types';
import {
  LEAGUE_CLUBS,
  INITIAL_USER_SQUAD,
  INITIAL_FREE_AGENTS,
  OPPONENT_STARS,
  generateLeagueMatchdays,
  generateInitialStandings,
} from '../../../data/initialData';
import { INITIAL_LEAGUES, CURRENT_USER } from '../../../data/mockLeagues';
import { I18nService } from '../i18n/i18n.service';
import { environment } from '../../../environments/environment';

import { CoachesState } from './coaches.state';

export type { TacticalFormation } from './squad.state';
export { FORMATION_SLOTS } from './squad.state';
export type { ActiveTab } from './ui.state';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  readonly i18n = inject(I18nService);
  readonly auth = inject(AuthState);
  readonly leaguesState = inject(LeaguesState);
  readonly squad = inject(SquadState);
  readonly market = inject(MarketState);
  readonly economy = inject(EconomyState);
  readonly coaches = inject(CoachesState);
  readonly competitionService = inject(CompetitionService);
  readonly ui = inject(UiState);
  readonly router = inject(Router);
  readonly environment = environment;
  readonly appVersion = APP_VERSION;
  readonly appBuild = APP_BUILD;
  readonly appVersionTag = `${APP_VERSION}_${APP_BUILD}`;
  readonly appVersionLabel = APP_VERSION_LABEL;

  canAction(action: string): boolean { return clubCapabilities()?.actions[action]?.available === true; }
  actionReason(action: string): string { return this.canAction(action) ? '' : 'Acción no disponible en el estado actual del club.'; }
  readonly selectedClubProfile = signal<{ id: string; name?: string; code?: string } | null>(null);

  openClubProfile(clubId: string, name?: string, code?: string): void {
    if (!clubId) return;
    this.selectedClubProfile.set({ id: clubId, name, code });
  }

  closeClubProfile(): void {
    this.selectedClubProfile.set(null);
  }

  readonly isClubShieldEditorOpen = signal<boolean>(false);
  readonly editingShieldClub = signal<{ id: string; name?: string; code?: string; canAdminister?: boolean } | null>(null);

  openClubShieldEditor(clubId?: string, name?: string, code?: string, canAdminister?: boolean): void {
    const id = clubId || this.activeUserClubId() || this.userClub()?.id;
    if (!id) return;
    this.editingShieldClub.set({
      id,
      name: name || this.userClub()?.name,
      code: code || this.userClub()?.code,
      canAdminister,
    });
    this.isClubShieldEditorOpen.set(true);
  }

  closeClubShieldEditor(): void {
    this.isClubShieldEditorOpen.set(false);
    this.editingShieldClub.set(null);
  }

  onClubShieldSaved(shield: ClubShieldConfig, clubId?: string): void {
    const targetId = clubId || this.editingShieldClub()?.id || this.activeUserClubId() || this.userClub()?.id;
    if (targetId && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(`lom_club_shield_${targetId}`, JSON.stringify(shield));
      } catch {}
    }
    if (targetId) {
      this.squad.userShieldMap.update(previous => ({ ...previous, [targetId]: shield }));
    }
    const currentClub = this.userClub();
    if (currentClub && (currentClub.id === targetId || !targetId)) {
      currentClub.shield = shield;
      currentClub.primaryColor = shield.primaryColor;
      currentClub.secondaryColor = shield.secondaryColor;
    }
    this.ui.showNotification('¡Escudo personalizado guardado correctamente!', 'success');
  }

  /**
   * Limpia completamente el contexto de juego (club, liga, competición, economía, mercado y storage)
   * para aislar de forma estricta las sesiones de distintos usuarios.
   */
  clearGameContext(): void {
    clearClubSelection();
    newsApi.clearCache();
    this.leaguesState.currentLeague.set(null);
    this.leaguesState.leagues.set([]);
    this.leaguesState.matchdays.set([]);
    this.leaguesState.standings.set([]);
    this.squad.resetClubState();
    this.squad.clubs.set([]);
    this.squad.allPlayers.set([]);
    this.economy.resetEconomyState();
    this.market.resetMarketState();
    this.coaches.clearState();
    this.competitionService.clearContext();

    if (typeof localStorage !== 'undefined') {
      const keysToRemove = [
        'fm_real_user_club_id',
        'fm_real_selected_league',
        'fm_real_competition_unit_id',
        'fm_real_competition_group_id',
        'fm_real_club_name',
        'fm_real_club_code',
        'fm_real_division_name',
        'fm_real_api_leagues',
        'football_manager_real_v01_clubs',
        'test_clubId',
        'test_seasonId',
        'test_competitionGroupId',
      ];
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  }

  // --- UI & State delegators ---
  readonly activeTab = this.ui.activeTab;
  readonly notification = this.ui.notification;
  readonly notificationsEnabled = this.ui.notificationsEnabled;
  readonly isDarkMode = this.ui.isDarkMode;
  readonly apiLogs = this.ui.apiLogs;

  t(key: string, params?: Record<string, string | number>): string {
    return this.ui.t(key, params);
  }
  setActiveTab(tab: ActiveTab) {
    this.ui.setActiveTab(tab);
  }
  showNotification(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') {
    this.ui.showNotification(message, type);
  }
  clearNotification() {
    this.ui.clearNotification();
  }
  dismissNotification() {
    this.ui.dismissNotification();
  }
  toggleNotifications() {
    this.ui.toggleNotifications();
  }
  setNotificationsEnabled(enabled: boolean) {
    this.ui.setNotificationsEnabled(enabled);
  }
  clearApiLogs() {
    this.ui.clearApiLogs();
  }
  toggleDarkMode() {
    this.ui.toggleDarkMode();
  }

  // --- Auth & Users delegators ---
  readonly currentUser = this.auth.currentUser;
  readonly isAuthLoading = this.auth.isAuthLoading;
  readonly isAuthenticated = this.auth.isAuthenticated;

  async login(user: string, password?: string) {
    this.clearGameContext();
    return this.auth.login(user, password);
  }

  async register(payload: { email: string; password: string }) {
    return this.auth.register(payload);
  }

  async resendRegistration(payload: { email: string }) {
    return this.auth.resendRegistration(payload);
  }

  async verifyRegistration(payload: { token: string }) {
    return this.auth.verifyRegistration(payload);
  }

  async handleSocialLoginSuccess(userData: { id: string; email: string; username?: string | null }) {
    this.clearGameContext();
    return this.auth.handleSocialLoginSuccess(userData);
  }

  async logout(): Promise<void> {
    try {
      await this.auth.logout();
    } finally {
      this.clearGameContext();
      await this.router.navigate(['/login']);
    }
  }

  // --- Leagues & Competitions delegators ---
  readonly leagues = this.leaguesState.leagues;
  readonly currentLeague = this.leaguesState.currentLeague;
  readonly isLeagueLoading = this.leaguesState.isLeagueLoading;
  readonly matchdays = this.leaguesState.matchdays;
  readonly currentMatchdayNumber = this.leaguesState.currentMatchdayNumber;
  readonly standings = this.leaguesState.standings;
  readonly editorialRatings = this.leaguesState.editorialRatings;
  readonly currentMatchday = this.leaguesState.currentMatchday;

  setMatchdays(matchdays: Matchday[]) {
    this.leaguesState.setMatchdays(matchdays);
  }

  setStandings(standings: StandingsRow[]) {
    this.leaguesState.setStandings(standings);
  }

  returnToLeagueSelection() {
    this.leaguesState.returnToLeagueSelection();
    this.router.navigate(['/leagues']);
  }
  syncWithCompetitions(state: any) {
    this.leaguesState.syncWithCompetitions(state);
  }
  setUserClubDivision(division: string) {
    this.squad.setUserClubDivision(division);
  }
  setUserClubInfo(name?: string, division?: string, code?: string) {
    this.squad.setUserClubInfo(name, division, code);
  }
  lockMatchday() {
    this.leaguesState.lockMatchday();
  }
  updateEditorialRating(playerId: string, updates: any) {
    this.leaguesState.updateEditorialRating(playerId, updates);
  }
  publishEditorialRatings() {
    this.leaguesState.publishEditorialRatings();
  }
  simulateCurrentMatchday() {
    this.leaguesState.simulateCurrentMatchday();
  }
  nextMatchday() {
    this.leaguesState.nextMatchday();
  }

  // --- Squad, Clubs & Tactics delegators ---
  readonly clubs = this.squad.clubs;
  readonly allPlayers = this.squad.allPlayers;
  readonly activeFormation = this.squad.activeFormation;
  readonly availableFormations = this.squad.availableFormations;
  readonly formationsData = this.squad.formationsData;
  readonly isFormationsLoading = this.squad.isFormationsLoading;
  readonly lineupSlots = this.squad.lineupSlots;
  readonly userClub = this.squad.userClub;
  readonly userSquad = this.squad.userSquad;
  readonly freeAgents = this.squad.freeAgents;
  readonly opponentStars = this.squad.opponentStars;
  readonly lineupVersion = this.squad.lineupVersion;
  readonly isSquadLoading = this.squad.isSquadLoading;
  readonly activeUserClubId = this.squad.activeUserClubId;
  readonly loadedSquadClubId = this.squad.loadedSquadClubId;
  readonly lineupBench = this.squad.lineupBench;
  readonly lineupSelectionVersion = this.squad.lineupSelectionVersion;

  setActiveUserClubId(clubId: string | null) {
    this.squad.setActiveUserClubId(clubId);
  }
  fetchSquadForClub(clubId: string, forceRefresh: boolean = false) {
    return this.squad.fetchSquadForClub(clubId, forceRefresh);
  }
  fetchClubLineup(clubId: string, forceRefresh: boolean = false) {
    return this.squad.fetchClubLineup(clubId, forceRefresh);
  }
  fetchFormations(forceRefresh: boolean = false) {
    return this.squad.fetchFormations(forceRefresh);
  }
  setFormation(formation: TacticalFormation) {
    this.squad.setFormation(formation);
  }
  assignPlayerToSlot(slotId: string, playerId: string | null) {
    this.squad.assignPlayerToSlot(slotId, playerId);
  }
  autoPickLineup(formation?: string) {
    return this.squad.autoPickLineup(formation);
  }
  saveLineup() {
    return this.squad.saveLineup();
  }
  reloadLineup() {
    return this.squad.reloadLineup();
  }
  addBenchPlayer(playerId: string) {
    return this.squad.addBenchPlayer(playerId);
  }
  removeBenchPlayer(playerId: string) {
    return this.squad.removeBenchPlayer(playerId);
  }
  toggleBenchPlayer(playerId: string) {
    return this.squad.toggleBenchPlayer(playerId);
  }
  clearBench() {
    return this.squad.clearBench();
  }
  listPlayerForSale(playerId: string, askingPrice: number) {
    return this.squad.listPlayerForSale(playerId, askingPrice);
  }
  removePlayerFromSale(playerId: string) {
    return this.squad.removePlayerFromSale(playerId);
  }
  readonly transferPending = this.squad.transferPending;

  // --- Market & Transfers delegators ---
  readonly transferOffers = this.market.transferOffers;
  readonly isMarketLoading = this.squad.isMarketLoading;
  readonly marketListingInfo = this.squad.marketListingInfo;
  readonly marketError = this.squad.marketError;

  fetchFreeAgentsListing(competitionGroupId: string, forceRefresh: boolean = false) {
    return this.squad.fetchFreeAgentsListing(competitionGroupId, forceRefresh);
  }

  submitPlayerOffer(
    listingId: string,
    playerInstanceId: string,
    payload: {
      transferAmount: number;
      annualSalary: number;
      releaseClause: number;
      termSeasons: number;
    },
    player?: Player
  ) {
    return this.market.submitPlayerOffer(listingId, playerInstanceId, payload, player);
  }

  cancelOffer(offerId: string) {
    return this.market.cancelOffer(offerId);
  }

  updatePlayerOffer(
    offerIdOrListingId: string,
    payloadOrPlayerInstanceId: any,
    listingIdOrPayload?: any,
    offerIdOrPlayerInstanceId?: any,
    player?: Player
  ) {
    return this.market.updatePlayerOffer(
      offerIdOrListingId,
      payloadOrPlayerInstanceId,
      listingIdOrPayload,
      offerIdOrPlayerInstanceId,
      player
    );
  }

  makeTransferOffer(
    playerId: string,
    transferFeeM: number,
    salaryM: number,
    durationYears: number,
    clauseM: number,
    isBuyout: boolean = false,
    signingBonusM: number = 0
  ) {
    return this.market.makeTransferOffer(
      playerId,
      transferFeeM,
      salaryM,
      durationYears,
      clauseM,
      isBuyout,
      signingBonusM
    );
  }
  buyoutReleaseClause(playerId: string) {
    return this.market.buyoutReleaseClause(playerId);
  }
  refreshDailyFreeAgents() {
    this.market.refreshDailyFreeAgents();
  }

  // --- Active Offers, Historical Offers & Watchlist delegators ---
  readonly activeOffersData = this.market.activeOffersData;
  readonly isActiveOffersLoading = this.market.isActiveOffersLoading;
  readonly activeOffersError = this.market.activeOffersError;

  readonly historicalOffersData = this.market.historicalOffersData;
  readonly isHistoricalOffersLoading = this.market.isHistoricalOffersLoading;
  readonly historicalOffersError = this.market.historicalOffersError;

  readonly watchlistPlayerIds = this.market.watchlistPlayerIds;

  readonly portfolioItems = this.market.portfolioItems;
  readonly isPortfolioLoading = this.market.isPortfolioLoading;
  readonly portfolioError = this.market.portfolioError;
  readonly portfolioCount = this.market.portfolioCount;
  readonly hasMaxPortfolio = this.market.hasMaxPortfolio;
  readonly pendingReleaseClauseAttempts = this.market.pendingReleaseClauseAttempts;
  readonly pendingContractRenewals = this.market.pendingContractRenewals;

  fetchActiveOffers(status: string = 'active') {
    return this.market.fetchActiveOffers(status);
  }
  fetchHistoricalOffers(page: number = 0, size: number = 50) {
    return this.market.fetchHistoricalOffers(page, size);
  }
  fetchPortfolio(clubId?: string, forceRefresh: boolean = false) {
    return this.market.fetchPortfolio(clubId, forceRefresh);
  }
  addToPortfolio(player: Player, clubId?: string) {
    return this.market.addToPortfolio(player, clubId);
  }
  removeFromPortfolio(playerId: string, clubId?: string) {
    return this.market.removeFromPortfolio(playerId, clubId);
  }
  submitReleaseClauseAttempt(payload: ReleaseClauseAttemptPayload, clubId?: string) {
    return this.market.submitReleaseClauseAttempt(payload, clubId);
  }
  submitContractRenewal(payload: ContractRenewalPayload, clubId?: string) {
    return this.market.submitContractRenewal(payload, clubId);
  }
  resolveDailyMarket(clubId?: string) {
    return this.market.resolveDailyMarket(clubId);
  }
  toggleWatchlist(playerId: string) {
    return this.market.toggleWatchlist(playerId);
  }
  isInWatchlist(playerId: string): boolean {
    return this.market.isInWatchlist(playerId);
  }

  // --- Stadium & Economy delegators ---
  readonly stadiumData = this.economy.stadiumData;
  readonly staffData = this.economy.staffData;
  readonly balanceData = this.economy.balanceData;
  readonly ledgerData = this.economy.ledgerData;
  readonly catalogData = this.economy.catalogData;
  readonly constructionData = this.economy.constructionData;
  readonly isStadiumLoading = this.economy.isStadiumLoading;
  readonly isStaffLoading = this.economy.isStaffLoading;
  readonly isEconomyLoading = this.economy.isEconomyLoading;
  readonly isCatalogLoading = this.economy.isCatalogLoading;
  readonly isConstructionLoading = this.economy.isConstructionLoading;
  readonly loadedStadiumClubId = this.economy.loadedStadiumClubId;
  readonly loadedStaffClubId = this.economy.loadedStaffClubId;
  readonly loadedEconomyClubId = this.economy.loadedEconomyClubId;
  readonly loadedCatalogClubId = this.economy.loadedCatalogClubId;

  readonly ticketPriceData = this.economy.ticketPriceData;
  readonly isTicketPriceLoading = this.economy.isTicketPriceLoading;
  readonly loadedTicketPriceClubId = this.economy.loadedTicketPriceClubId;

  // Sponsors signals
  readonly sponsorOffers = this.economy.sponsorOffers;
  readonly sponsorContracts = this.economy.sponsorContracts;
  readonly isSponsorOffersLoading = this.economy.isSponsorOffersLoading;
  readonly isSponsorContractsLoading = this.economy.isSponsorContractsLoading;
  readonly sponsorOffersError = this.economy.sponsorOffersError;
  readonly sponsorOffersStatus = this.economy.sponsorOffersStatus;
  readonly sponsorContractsError = this.economy.sponsorContractsError;
  readonly activeSponsorContracts = this.economy.activeSponsorContracts;
  readonly occupiedSponsorTypes = this.economy.occupiedSponsorTypes;

  fetchClubStadium(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchClubStadium(clubId, forceRefresh);
  }
  fetchClubTicketPrice(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchClubTicketPrice(clubId, forceRefresh);
  }
  fetchStadiumCatalog(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchStadiumCatalog(clubId, forceRefresh);
  }
  fetchActiveConstruction(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchActiveConstruction(clubId, forceRefresh);
  }
  fetchClubStaff(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchClubStaff(clubId, forceRefresh);
  }
  fetchClubBalance(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchClubBalance(clubId, forceRefresh);
  }
  fetchClubLedger(clubId: string, page?: number, size?: number) {
    return this.economy.fetchClubLedger(clubId, page, size);
  }
  fetchClubEconomy(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchClubEconomy(clubId, forceRefresh);
  }

  updateTicketPricing(ticketPrice: number, expectedVersion?: number) {
    return this.economy.updateTicketPricing(ticketPrice, expectedVersion);
  }
  upgradeStadiumCapacity(newCapacity: number) {
    if (confirmedClub()?.mode === 'PRIVATE') return {success:false,message:'Esta operación antigua no está disponible para clubes privados.'};
    return this.economy.upgradeStadiumCapacity(newCapacity);
  }
  upgradeCommercialModule(moduleType: 'parking' | 'cafeteria' | 'merchandising') {
    if (confirmedClub()?.mode === 'PRIVATE') return {success:false,message:'Esta operación antigua no está disponible para clubes privados.'};
    return this.economy.upgradeCommercialModule(moduleType);
  }
  requestBankLoan(amountM: number, termSeasons: number) {
    if (confirmedClub()?.mode === 'PRIVATE') return {success:false,message:'Esta operación antigua no está disponible para clubes privados.'};
    return this.economy.requestBankLoan(amountM, termSeasons);
  }
  payMonthlySubscription() {
    if (confirmedClub()?.mode === 'PRIVATE') return {success:false,message:'Esta operación antigua no está disponible para clubes privados.'};
    return this.economy.payMonthlySubscription();
  }
  topUpCredits(amountCr: number) {
    return this.economy.topUpCredits(amountCr);
  }

  fetchSponsorOffers(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchSponsorOffers(clubId, forceRefresh);
  }
  fetchSponsorContracts(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchSponsorContracts(clubId, forceRefresh);
  }
  contractSponsorOffer(clubId: string, offerId: string, requestId: string) {
    return this.economy.contractSponsorOffer(clubId, offerId, requestId);
  }

  // Matchday Loans signals & methods
  readonly matchdayLoanOffer = this.economy.matchdayLoanOffer;
  readonly activeMatchdayLoan = this.economy.activeMatchdayLoan;
  readonly matchdayLoansHistory = this.economy.matchdayLoansHistory;
  readonly aggregatedDebt = this.economy.aggregatedDebt;
  readonly economicSummary = this.economy.economicSummary;
  readonly isLoansLoading = this.economy.isLoansLoading;
  readonly loansError = this.economy.loansError;

  fetchMatchdayLoanOffer(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchMatchdayLoanOffer(clubId, forceRefresh);
  }
  fetchMatchdayLoansHistory(clubId: string, page: number = 0, size: number = 20) {
    return this.economy.fetchMatchdayLoansHistory(clubId, page, size);
  }
  fetchAggregatedDebt(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchAggregatedDebt(clubId, forceRefresh);
  }
  fetchEconomicSummary(clubId: string, forceRefresh: boolean = false) {
    return this.economy.fetchEconomicSummary(clubId, forceRefresh);
  }
  contractMatchdayLoan(clubId: string, principal: number, requestId: string) {
    return this.economy.contractMatchdayLoan(clubId, principal, requestId);
  }
  repayMatchdayLoan(clubId: string, loanId: string, amount: number, requestId: string) {
    return this.economy.repayMatchdayLoan(clubId, loanId, amount, requestId);
  }

  getNewsByUnit(competitionUnitId: string, params?: { cursor?: string; limit?: number }, forceRefresh = false,
    onCached?: (response: NewsResponse) => void) {
    return newsApi.getNewsByUnit(competitionUnitId, params, forceRefresh, onCached);
  }

  constructor() {
    // Connect Auth with LeaguesState so user leagues populate on login
    this.auth.setLeaguesHandler((leagues) => {
      this.leaguesState.setLeagues(leagues);
    });

    // Reset game context when session expires or user logs out
    this.auth.setSessionExpiredHook(() => {
      this.clearGameContext();
    });

    this.auth.setLogoutHook(() => {
      this.clearGameContext();
    });

    this.squad.setOnUserClubChange((clubId) => {
      this.economy.fetchClubBalance(clubId);
    });


  }

  resetAllData(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.clear();
    }
    this.auth.currentUser.set(CURRENT_USER);
    this.leaguesState.currentLeague.set(null);
    this.squad.clubs.set(LEAGUE_CLUBS);
    this.squad.allPlayers.set([
      ...INITIAL_USER_SQUAD,
      ...INITIAL_FREE_AGENTS,
      ...OPPONENT_STARS,
    ]);
    this.leaguesState.matchdays.set(generateLeagueMatchdays(LEAGUE_CLUBS));
    this.leaguesState.currentMatchdayNumber.set(1);
    this.leaguesState.standings.set(generateInitialStandings(LEAGUE_CLUBS));
    this.squad.autoPickLineup();
    this.ui.showNotification(
      'Todos los datos se han restablecido al estado inicial',
      'info'
    );
  }
}
